import { Beast } from '../models/Beast';
import { Surroundings } from '../models/Surroundings';
import { landThresholds as thresholds, BEAST } from '../models/constants';
import type { IBeastState } from './states/beast/IBeastState';

/**
 * Advances a Beast one simulation tick through its state machine.
 * Mirrors BeingController but uses IBeastState handlers and beast-specific
 * health-decay rules (heavier base decay, extra decay in lethal terrain, none at all
 * while spawn immunity holds). Kill
 * attribution lives in the Beast itself; this controller only ages the hunt-credit
 * window, at the end of the tick so a blow landed this tick always still counts.
 */
export class BeastController {
    private readonly handlers: Map<string, IBeastState>;
    private readonly interrupts: Array<[string, IBeastState]>;

    constructor({ states }: { states: Map<string, IBeastState> }) {
        this.handlers = states;
        this.interrupts = [...states.entries()].filter(([, s]) => s.allowInterrupt);
    }

    tick(beast: Beast, surroundings: Surroundings): void {
        this.updateImmunity(beast, surroundings);

        if (!beast.hasImmunity) {
            this.applyHealthDecay(beast, surroundings.altitude);
        }

        if (beast.health > 0) {
            const interrupt = this.interrupts.find(([, s]) => s.canEnter(beast, surroundings));
            if (interrupt) {
                beast.state = interrupt[0];
            }

            const handler = this.handlers.get(beast.state);
            if (handler) {
                beast.state = handler.tick(beast, surroundings);
            }
        }

        beast.ageHuntCredit();
    }

    /**
     * Clears spawn immunity on the first tick where the Beast is out of danger.
     * Beasts scattered into water or onto a mountain at level start keep immunity
     * until they reach safe ground, so they cannot die before fleeing has a chance
     * to work. Mirrors `BeingController.updateImmunity`.
     */
    private updateImmunity(beast: Beast, surroundings: Surroundings): void {
        if (beast.hasImmunity && !surroundings.isInDanger) {
            beast.hasImmunity = false;
        }
    }

    private applyHealthDecay(beast: Beast, altitude: number): void {
        let delta: number;
        if (altitude < thresholds.sea || altitude > thresholds.mountain) {
            delta = -BEAST.HEALTH_DECAY_DANGER;
        } else {
            delta = -BEAST.HEALTH_DECAY_BASE;
        }
        beast.setHealth(beast.health + delta);
    }
}
